import { generatePath } from '@toggle/helpers';
import { useNavigate } from 'react-router';

import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { appPaths, isAppBasePath } from '~/routes/app-paths';
import { queryKeys, queryValues } from '~/routes/query-params';
import { PosthogTrackingSource } from '~/services/tracking/posthog/constants';
import { create } from '~/utils/store/createStore';

const modes = ['quickAnalysis', 'document', 'research'] as const;
export type ChatMode = (typeof modes)[number];

type ChatModeStore = {
  chatMode: ChatMode;
  previousMode: ChatMode | null;
  alfredTrackingSource: PosthogTrackingSource | null;
  newConversationSource: PosthogTrackingSource | null;
  setChatMode: (chatMode: ChatMode) => void;
  resetPreviousMode: () => void;
};

export const useChatModeStore = create<ChatModeStore>(set => ({
  chatMode: 'research',
  previousMode: null,
  alfredTrackingSource: null,
  newConversationSource: null,
  setChatMode: chatMode =>
    set(state => ({
      previousMode: state.chatMode,
      chatMode,
    })),
  resetPreviousMode: () => set({ previousMode: null }),
}));

export const useChatNavigation = () => {
  const { chatMode, previousMode, setChatMode, resetPreviousMode } =
    useChatModeStore();
  const { params, setQueryParams } = useQueryParams();
  const navigate = useNavigate();

  const navigateToAlfred = (
    mode: ChatMode,
    searchParams = new URLSearchParams()
  ) => {
    setMode(mode);

    navigate({
      pathname: appPaths.base,
      search: searchParams.toString(),
    });
  };

  const setMode = (mode: ChatMode) => {
    const onAlfredPage = isAppBasePath(location.pathname);

    if (onAlfredPage) {
      setQueryParams({ [queryKeys.mode]: mode }, { replace: true });
    }

    setChatMode(mode);
  };

  const navigateToConversation = (conversationId: string, newTab = false) => {
    const url = generatePath({
      basePath: appPaths.base,
      params: {
        [queryKeys.mode]: queryValues.deepResearchMode,
        [queryKeys.conversationId]: conversationId,
      },
    });

    if (newTab) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    navigate(url);
  };

  const getMode = (): ChatMode => {
    const paramMode = params?.[queryKeys.mode];

    if (
      paramMode === 'quickAnalysis' ||
      paramMode === 'document' ||
      paramMode === 'research'
    ) {
      return paramMode;
    }

    if (chatMode !== 'research') {
      return chatMode;
    }

    return 'research';
  };

  return {
    navigateToAlfred,
    mode: getMode(),
    previousMode,
    setMode,
    navigateToConversation,
    resetPreviousMode,
  };
};
